import { Clock, ExternalLink, X } from "lucide-react";
import { VideoCamera } from "@phosphor-icons/react";
import type { CandidateWaitingPopup } from "@shared/candidateWaiting";
import { waitingCardText } from "@/lib/candidateWaitingAlerts";

/**
 * Карточка «Кандидат ждёт вас в комнате» — в ряду всплывашек справа.
 *
 * По образцу залов ожидания Zoom, Meet и doxy.me: в углу, не на весь экран,
 * сама не исчезает; сразу видно, кто, на какую позицию и с какого времени
 * ждёт; одна главная кнопка ведёт прямо в эту комнату. Кнопки «отказать» нет:
 * ссылка у кандидата личная, отсеивать некого.
 *
 * Вид — как у карточек кандидатов по Figma: Onest, тёмно-синяя кнопка-пилюля,
 * скругление 16, мягкая тень. На телефоне кнопки не ниже 44 px.
 */
export function CandidateWaitingCard({
  item,
  opensNewTab,
  currentRoom = false,
  offerNotifications,
  onEnter,
  onDismiss,
  onEnableNotifications,
}: {
  item: CandidateWaitingPopup;
  /** Сейчас открыта живая встреча: комната откроется в новой вкладке. */
  opensNewTab: boolean;
  currentRoom?: boolean;
  offerNotifications: boolean;
  onEnter: () => void;
  onDismiss: () => void;
  onEnableNotifications: () => void;
}) {
  const text = waitingCardText(item);
  return (
    <article
      aria-label={text.title}
      className="pointer-events-auto flex gap-3 rounded-2xl border border-[#eaecf0] bg-white p-4 font-['Onest','Inter',system-ui,sans-serif] shadow-[0_12px_32px_rgba(16,24,40,0.14)]"
    >
      <div className="relative h-9 w-9 shrink-0">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#000b3e]">
          <VideoCamera
            className="h-[18px] w-[18px] text-white"
            aria-hidden="true"
          />
        </div>
        {/* Зелёная точка — человек в комнате прямо сейчас. */}
        <span
          aria-hidden="true"
          className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-[#12b76a] ring-2 ring-white motion-safe:animate-pulse"
        />
      </div>
      <div className="min-w-0 flex-1">
        {/* Имена не обрезаем — переносим. */}
        <p className="text-[14px] font-semibold leading-5 text-[#101828]">
          {text.title}
        </p>
        {text.subtitle && (
          <p className="mt-0.5 text-[13px] leading-[18px] text-[#667085]">
            {text.subtitle}
          </p>
        )}
        {text.since && (
          <p className="mt-1 inline-flex items-center gap-1 text-[12px] leading-4 text-[#667085]">
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            {text.since}
          </p>
        )}
        <div>
          <button
            type="button"
            onClick={onEnter}
            title={
              opensNewTab
                ? "Откроется в новой вкладке — текущая встреча останется открытой"
                : undefined
            }
            className="mt-3 inline-flex min-h-11 items-center gap-1.5 rounded-full bg-[#000b3e] px-4 text-[13px] font-medium text-white transition-colors hover:bg-[#16204f] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#000b3e] sm:min-h-9"
          >
            {currentRoom ? "Войти во встречу" : "Войти в комнату"}
            {opensNewTab && (
              <>
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="sr-only"> (откроется в новой вкладке)</span>
              </>
            )}
          </button>
        </div>
        {offerNotifications && (
          <button
            type="button"
            onClick={onEnableNotifications}
            className="mt-2 py-1 text-left text-[12px] font-medium leading-4 text-[#000b3e] underline underline-offset-2 transition-colors hover:text-[#16204f]"
          >
            Включить уведомления на компьютере
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Скрыть"
        className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-[#667085] transition-colors hover:bg-[#f2f4f7] hover:text-[#101828] sm:-mr-1 sm:-mt-1 sm:h-8 sm:w-8"
      >
        <X className="h-4 w-4" />
      </button>
    </article>
  );
}
